"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.list = void 0;
const comparison_1 = require("../utils/comparison");
const create_fn_1 = require("../utils/create-fn");
const get_arg_1 = require("../utils/get-arg");
// NEW
/*
## fn.list.contains

Check if a list contains a value.

### Arguments

1. (array) The list to check.
2. (string|number|boolean) The value to check for.

Returns true if the list contains the value, otherwise false. (boolean)
*/
const contains = (0, create_fn_1.createFn)("fn.list.contains", (args, context) => {
    const value = (0, get_arg_1.getArgArray)(args, 1, context);
    const piece = (0, get_arg_1.getArgUnknown)(args, 2, context);
    return value.some((item) => item == piece);
});
// NEW
/*
## fn.list.containsany

Check if a list contains any of the values of a list.

### Arguments

1. (array) The list to check.
2. (array) a list of values to check for.

Returns true if the list contains any of the values from the second list of values, otherwise false. (boolean)
*/
const containsany = (0, create_fn_1.createFn)("fn.list.containsany", (args, context) => {
    const value = (0, get_arg_1.getArgArray)(args, 1, context);
    const pieces = (0, get_arg_1.getArgArray)(args, 2, context);
    for (const piece of pieces) {
        if (piece === undefined) {
            continue;
        }
        if (value.some((item) => item == piece)) {
            return true;
        }
    }
    return false;
});
// NEW
/*
## fn.list.operate

Perform an operation on a list.

### Arguments

1. (array) The list to operate on.
2. (string) The operator to use. Options are:
    - `==`
    - `!=`
    - `>`
    - `<`
    - `>=`
    - `<=`
    - `in`
    - `!in`
    - `and`
    - `or`
3. (string|number|boolean) The operand to use.

Returns a list of the results of the operation. (array)
*/
const operate = (0, create_fn_1.createFn)("fn.list.operate", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    const operator = (0, get_arg_1.getArgString)(args, 2, context);
    const operand = (0, get_arg_1.getArgUnknown)(args, 3, context);
    if ((operator === "==" ||
        operator === "!=" ||
        operator === "&gt;" ||
        operator === "&lt;" ||
        operator === "&gt;=" ||
        operator === "&lt;=") &&
        !comparison_1.SUPPORTED_COMPARISON_TYPES.includes(typeof operand)) {
        throw new Error(`cannot compare list items to ${typeof operand}`);
    }
    if (operator === "==") {
        const comparitorValue = (0, comparison_1.toCompareValue)(operand);
        return values.map((item) => item !== undefined ? (0, comparison_1.toCompareValue)(item) == comparitorValue : undefined);
    }
    if (operator === "!=") {
        const comparitorValue = (0, comparison_1.toCompareValue)(operand);
        return values.map((item) => item !== undefined ? (0, comparison_1.toCompareValue)(item) != comparitorValue : undefined);
    }
    if (operator === "&gt;") {
        return values.map((item) => {
            if (item === undefined ||
                item === "" ||
                operand === undefined ||
                operand === "") {
                return undefined;
            }
            return Number(item) > Number(operand);
        });
    }
    if (operator === "&lt;") {
        return values.map((item) => {
            if (item === undefined ||
                item === "" ||
                operand === undefined ||
                operand === "") {
                return undefined;
            }
            return Number(item) < Number(operand);
        });
    }
    if (operator === "&gt;=") {
        return values.map((item) => {
            if (item === undefined ||
                item === "" ||
                operand === undefined ||
                operand === "") {
                return undefined;
            }
            return Number(item) >= Number(operand);
        });
    }
    if (operator === "&lt;=") {
        return values.map((item) => {
            if (item === undefined ||
                item === "" ||
                operand === undefined ||
                operand === "") {
                return undefined;
            }
            return Number(item) <= Number(operand);
        });
    }
    if (operator === "in") {
        if (!Array.isArray(operand)) {
            throw new Error("'in' operand must be an array");
        }
        return values.map((item) => {
            if (item === undefined) {
                return undefined;
            }
            for (const value of operand) {
                if (item == value) {
                    return true;
                }
            }
            return false;
        });
    }
    if (operator === "!in") {
        if (!Array.isArray(operand)) {
            throw new Error("'!in' operand must be an array");
        }
        return values.map((item) => {
            if (item === undefined) {
                return undefined;
            }
            for (const value of operand) {
                if (item == value) {
                    return false;
                }
            }
            return true;
        });
    }
    if (operator === "and") {
        return values.map((item) => item !== undefined ? item && operand : undefined);
    }
    if (operator === "or") {
        return values.map((item) => item !== undefined ? item || operand : undefined);
    }
    throw new Error(`operator ${operator} is not supported`);
});
// NEW
/*
## fn.list.paralleloperate

Perform an operation on a list in parallel with another list of similar length.
An index item of the first list is operated on with an item of the second list
with the same index.

### Arguments

1. (array) The list to operate on.
2. (string) The operator to use. Options are:
    - `==`
    - `!=`
    - `>`
    - `<`
    - `>=`
    - `<=`
    - `and`
    - `or`
3. (array) The list to operate with.

Returns a list of the results of the operation. (array)
*/
const paralleloperate = (0, create_fn_1.createFn)("fn.list.paralleloperate", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    const operator = (0, get_arg_1.getArgString)(args, 2, context);
    const operands = (0, get_arg_1.getArgArray)(args, 3, context);
    const length = Math.max(values.length, operands.length);
    const result = [];
    if (operator === "==") {
        for (let i = 0; i < length; i++) {
            result[i] =
                values[i] !== undefined
                    ? (0, comparison_1.toCompareValue)(values[i]) == (0, comparison_1.toCompareValue)(operands[i])
                    : undefined;
        }
        return result;
    }
    if (operator === "!=") {
        for (let i = 0; i < length; i++) {
            result[i] =
                values[i] !== undefined
                    ? (0, comparison_1.toCompareValue)(values[i]) != (0, comparison_1.toCompareValue)(operands[i])
                    : undefined;
        }
        return result;
    }
    if (operator === "&gt;") {
        for (let i = 0; i < length; i++) {
            if (values[i] === undefined ||
                values[i] === "" ||
                operands[i] === undefined ||
                operands[i] === "") {
                result[i] = undefined;
            }
            else {
                result[i] = Number(values[i]) > Number(operands[i]);
            }
        }
        return result;
    }
    if (operator === "&lt;") {
        for (let i = 0; i < length; i++) {
            if (values[i] === undefined ||
                values[i] === "" ||
                operands[i] === undefined ||
                operands[i] === "") {
                result[i] = undefined;
            }
            else {
                result[i] = Number(values[i]) < Number(operands[i]);
            }
        }
        return result;
    }
    if (operator === "&gt;=") {
        for (let i = 0; i < length; i++) {
            if (values[i] === undefined ||
                values[i] === "" ||
                operands[i] === undefined ||
                operands[i] === "") {
                result[i] = undefined;
            }
            else {
                result[i] = Number(values[i]) >= Number(operands[i]);
            }
        }
        return result;
    }
    if (operator === "&lt;=") {
        for (let i = 0; i < length; i++) {
            if (values[i] === undefined ||
                values[i] === "" ||
                operands[i] === undefined ||
                operands[i] === "") {
                result[i] = undefined;
            }
            else {
                result[i] = Number(values[i]) <= Number(operands[i]);
            }
        }
        return result;
    }
    if (operator === "and") {
        for (let i = 0; i < length; i++) {
            result[i] =
                values[i] !== undefined ? values[i] && operands[i] : undefined;
        }
        return result;
    }
    if (operator === "or") {
        for (let i = 0; i < length; i++) {
            result[i] =
                values[i] !== undefined ? values[i] || operands[i] : undefined;
        }
        return result;
    }
    throw new Error(`operator ${operator} is not supported`);
});
// NEW
/*
## fn.list.sum

Sum the values of a list.

### Arguments

1. (array) The list of values to sum.

Returns the sum of the values. (number)
*/
const sum = (0, create_fn_1.createFn)("fn.list.sum", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    return values.reduce((acc, item) => item === undefined || Number.isNaN(Number(item))
        ? acc
        : acc + Number(item), 0);
});
// NEW
/*
## fn.list.slice

Slice a list.

### Arguments

1. (array) The list to slice.
2. (number) Zero-based index at which to start extraction.
  Negative index counts back from the end of the array.
3. (number) Zero-based index at which to end extraction.
  Will extract up to but not including end index.

Returns the sliced list. (array)

Reference [Array.slice](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/slice) for more information.
*/
const slice = (0, create_fn_1.createFn)("fn.list.slice", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    const start = (0, get_arg_1.getArgNumberOrUndefined)(args, 2, context);
    const end = (0, get_arg_1.getArgNumberOrUndefined)(args, 3, context);
    return values.slice(start, end);
});
// NEW
/*
## fn.list.length

Get the length of a list.

### Arguments

1. (array) The list to get the length of.

Returns the length of the list. (number)
*/
const length = (0, create_fn_1.createFn)("fn.list.length", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    return values.length;
});
// NEW
/*
## fn.list.indexof

Get the index of a value in a list.

### Arguments

1. (array) The list to search.
2. (string|number|boolean) The value to search for.
3. (number) The index to start the search from. Optional.

Returns the index of the value in the list, or -1 if not found. (number)
*/
const indexof = (0, create_fn_1.createFn)("fn.list.indexof", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    const piece = (0, get_arg_1.getArgUnknown)(args, 2, context);
    const fromIndex = (0, get_arg_1.getArgNumberOrUndefined)(args, 3, context);
    return values.indexOf(piece, fromIndex);
});
// NEW
/*
## fn.list.unique

Get the unique values of a list.

### Arguments

1. (array) The list to get the unique values of.

Returns the unique values of the list. (array)
*/
const unique = (0, create_fn_1.createFn)("fn.list.unique", (args, context) => {
    const values = (0, get_arg_1.getArgArray)(args, 1, context);
    return Array.from(new Set(values));
});
// NEW
/*
## fn.list.concat

Concatenate two or more lists.

### Arguments

1-Infinity. (array) The lists to concatenate.

Returns the concatenated list. (array)
*/
const concat = (0, create_fn_1.createFn)("fn.list.concat", (args, context) => {
    const arrays = args.map((arg, index) => (0, get_arg_1.getArgArray)(args, index + 1, context));
    return arrays.flat();
});
// NEW
/*
## fn.list.fromMapKeys

Get the keys of a map as a list.

### Arguments

1. (map) The map to get the keys of.

Returns the keys of the map as a list. (array)
*/
const frommapkeys = (0, create_fn_1.createFn)("fn.list.frommapkeys", (args, context) => {
    const map = (0, get_arg_1.getArgMap)(args, 1, context);
    return Array.from(map.keys());
});
// NEW
/*
## fn.list.fromMapValues

Get the values of a map as a list.

### Arguments

1. (map) The map to get the values of.

Returns the values of the map as a list. (array)
*/
const frommapvalues = (0, create_fn_1.createFn)("fn.list.frommapvalues", (args, context) => {
    const map = (0, get_arg_1.getArgMap)(args, 1, context);
    return Array.from(map.values());
});
exports.list = {
    contains,
    containsany,
    operate,
    paralleloperate,
    sum,
    slice,
    length,
    indexof,
    unique,
    concat,
    frommapkeys,
    frommapvalues,
};
//# sourceMappingURL=list.js.map